import type { IntegrationProvider } from "../runtime/ChatRuntime";
import { isString, safeHttpsUrl } from "./utils/schemaUtils";

const PROVIDERS: ReadonlySet<string> = new Set<IntegrationProvider>(["google", "slack", "notion", "linear", "github"]);

const isProvider = (v: unknown): v is IntegrationProvider => isString(v) && PROVIDERS.has(v);

interface SuggestionBase {
  name: string;
  /** The model's one sentence on what connecting would let it do. */
  reason: string;
  /** Only an organization owner can set the integration up. */
  ownerOnly: boolean;
}

// One suggestion per suggest_integration call. An mcp suggestion names a server
// already deployed to the project that the user has not connected their account
// to; a builtin one names a first-party provider the organization has not set up.
export type IntegrationSuggestionPayload =
  | (SuggestionBase & { kind: "mcp"; serverId: string; connectUrl: string; iconUrl?: string })
  | (SuggestionBase & { kind: "builtin"; provider: IntegrationProvider });

export const parseIntegrationSuggestionPayload = (v: unknown): IntegrationSuggestionPayload | null => {
  if (!v || typeof v !== "object") return null;
  const r = v as Record<string, unknown>;
  if (r.type !== "integration_suggestion") return null;
  if (!isString(r.name) || !isString(r.reason) || typeof r.ownerOnly !== "boolean") return null;
  const base = { name: r.name, reason: r.reason, ownerOnly: r.ownerOnly };
  if (r.kind === "mcp" && isString(r.serverId) && isString(r.connectUrl)) {
    // The icon is loaded straight into an img, so only an https URL is kept.
    const iconUrl = isString(r.iconUrl) ? safeHttpsUrl(r.iconUrl) : undefined;
    return { ...base, kind: "mcp", serverId: r.serverId, connectUrl: r.connectUrl, iconUrl };
  }
  if (r.kind === "builtin" && isProvider(r.provider)) return { ...base, kind: "builtin", provider: r.provider };
  return null;
};
